<script setup>
import { ref, onMounted, computed } from 'vue'
import { useUserStore } from '../stores/userStore'
import AddUserForm from './AddUserForm.vue'
import ApiTokenManager from './ApiTokenManager.vue'
import TokenPopup from './TokenPopup.vue'
import axios from '../axios.js'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close'])

const userStore = useUserStore()
const activeTab = ref('users') // users, packages, roles, permissions, api-tokens
const selectedUser = ref(null)
const showUserDetails = ref(false)
const searchQuery = ref('')
const selectedRole = ref('')
const selectedOrganization = ref('')
const showAddUserForm = ref(false)
const updatingPermissions = ref(false)

// Organization team modal
const showOrganizationTeamModal = ref(false)
const selectedOrganizationTeam = ref(null)
const organizationTeamUsers = ref([])

// Computed property for filtered users
const filteredUsers = computed(() => {
  let users = userStore.users || []

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    users = users.filter(user => 
      (user.name || '').toLowerCase().includes(query) ||
      (user.email || '').toLowerCase().includes(query) ||
      (user.organization || '').toLowerCase().includes(query)
    )
  }

  // Filter by role
  if (selectedRole.value) {
    users = users.filter(user => user.role === selectedRole.value)
  }

  // Filter by organization
  if (selectedOrganization.value) {
    users = users.filter(user => user.organization === selectedOrganization.value)
  }

  return users
})

// Computed property for users grouped by organization
const usersByOrganization = computed(() => {
  const users = filteredUsers.value
  const grouped = {}
  
  users.forEach(user => {
    const org = user.organization || 'No Organization'
    if (!grouped[org]) {
      grouped[org] = []
    }
    grouped[org].push(user)
  })
  
  // Sort organizations with OC first, then alphabetically, with "No Organization" last
  const sortedOrgs = Object.keys(grouped).sort((a, b) => {
    // OC group always comes first (case insensitive)
    if (a.toLowerCase() === 'oc') return -1
    if (b.toLowerCase() === 'oc') return 1
    
    // "No Organization" always comes last
    if (a === 'No Organization') return 1
    if (b === 'No Organization') return -1
    
    // All other organizations sorted alphabetically
    return a.localeCompare(b)
  })
  
  return sortedOrgs.map(org => ({
    name: org,
    users: grouped[org],
    count: grouped[org].length
  }))
})

// Computed property for available organizations
const availableOrganizations = computed(() => {
  const orgs = [...new Set(userStore.users.map(user => user.organization).filter(Boolean))]
  return orgs.sort()
})

// Computed property for filtered permissions
const filteredPermissions = computed(() => {
  if (!permissionsSearchQuery.value) {
    return permissions.value
  }

  const query = permissionsSearchQuery.value.toLowerCase()
  return permissions.value.filter(permission => 
    permission.name.toLowerCase().includes(query) ||
    permission.permission_key.toLowerCase().includes(query) ||
    permission.category.toLowerCase().includes(query) ||
    (permission.description || '').toLowerCase().includes(query)
  )
})

// Computed property for filtered user permissions
const filteredUserPermissions = computed(() => {
  if (!userPermissionsSearchQuery.value) {
    return permissions.value
  }

  const query = userPermissionsSearchQuery.value.toLowerCase()
  return permissions.value.filter(permission => 
    permission.name.toLowerCase().includes(query) ||
    permission.permission_key.toLowerCase().includes(query) ||
    permission.category.toLowerCase().includes(query) ||
    (permission.description || '').toLowerCase().includes(query)
  )
})

// Computed property for filtered leagues
const filteredLeagues = computed(() => {
  if (!leaguesSearchQuery.value) {
    return availableLeagues.value
  }

  const query = leaguesSearchQuery.value.toLowerCase()
  return availableLeagues.value.filter(league => 
    (league.name || '').toLowerCase().includes(query) ||
    (league.sport || '').toLowerCase().includes(query) ||
    (league.country || '').toLowerCase().includes(query)
  )
})

onMounted(() => {
  userStore.fetchUsers()
  // fetchPermissions()
  if (props.show) {
    userStore.fetchUsers()
  }
})

const viewUserDetails = (user) => {
  selectedUser.value = user
  showUserDetails.value = true
  fetchLeagues()
}

const closeUserDetails = () => {
  selectedUser.value = null
  showUserDetails.value = false
}

// Organization team modal functions
const viewOrganizationTeam = (organization) => {
  selectedOrganizationTeam.value = organization
  organizationTeamUsers.value = userStore.users.filter(user => 
    (user.organization || 'No Organization') === organization
  )
  showOrganizationTeamModal.value = true
}

const closeOrganizationTeamModal = () => {
  showOrganizationTeamModal.value = false
  selectedOrganizationTeam.value = null
  organizationTeamUsers.value = []
}



const handleUserUpdated = (updatedUser) => {
  // Update the user in the local list
  const userIndex = userStore.users.findIndex(u => u.id === updatedUser.id)
  if (userIndex !== -1) {
    userStore.users[userIndex] = { ...userStore.users[userIndex], ...updatedUser }
  }
  
  // Update the selected user
  selectedUser.value = { ...selectedUser.value, ...updatedUser }
  
  // Refresh the users list to ensure we have the latest data
  userStore.fetchUsers()
}

// Database-driven permissions
const permissions = ref([])
const permissionsByCategory = ref({})
const loadingPermissions = ref(false)
const permissionsSearchQuery = ref('')
const selectedPermissionKey = ref('')
const showPermissionUsersModal = ref(false)
const usersWithSelectedPermission = ref([])
const userPermissionsSearchQuery = ref('')
const showAddPermissionModal = ref(false)
const newPermission = ref({
  permission_key: '',
  name: '',
  description: '',
  category: 'general',
  level: 1,
  is_active: true,
  is_system: false,
  sort_order: 0
})
const addingPermission = ref(false)

// Password change functionality
const showPasswordChangeModal = ref(false)
const passwordChangeForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})
const changingPassword = ref(false)
const passwordChangeError = ref('')
const passwordChangeSuccess = ref(false)

// League access management
const availableLeagues = ref([])
const loadingLeagues = ref(false)
const updatingLeagueAccess = ref(false)
const leaguesSearchQuery = ref('')

// Category metadata for icons and descriptions
const categoryMetadata = {
  'users': {
    icon: 'fa-users',
    description: 'User management and administration',
    color: 'blue'
  },
  'trading': {
    icon: 'fa-chart-line',
    description: 'Trading features and tools',
    color: 'green'
  },
  'markets': {
    icon: 'fa-store',
    description: 'Market operations and management',
    color: 'purple'
  },
  'fixtures': {
    icon: 'fa-calendar-alt',
    description: 'Fixture and game management',
    color: 'orange'
  },
  'teams': {
    icon: 'fa-users-rectangle',
    description: 'Team information and management',
    color: 'teal'
  },
  'players': {
    icon: 'fa-user-tie',
    description: 'Player management and statistics',
    color: 'indigo'
  },
  'rosters': {
    icon: 'fa-list-ul',
    description: 'Team rosters and lineups',
    color: 'cyan'
  },
  'reports': {
    icon: 'fa-chart-bar',
    description: 'Reporting and data export',
    color: 'red'
  },
  'analytics': {
    icon: 'fa-chart-pie',
    description: 'Analytics dashboard and insights',
    color: 'pink'
  },
  'exports': {
    icon: 'fa-download',
    description: 'Data export functionality',
    color: 'gray'
  },
  'admin': {
    icon: 'fa-shield-alt',
    description: 'System administration',
    color: 'yellow'
  },
  'navigation': {
    icon: 'fa-compass',
    description: 'Navigation and menu access',
    color: 'emerald'
  },
  'special': {
    icon: 'fa-crown',
    description: 'Special access permissions',
    color: 'amber'
  }
}

// Fetch permissions from database
const fetchPermissions = async () => {
  loadingPermissions.value = true
  try {
    // Use userStore method that handles authentication and response parsing
    const data = await userStore.fetchAvailablePermissions()
    
    // Check if we got database permissions or need to convert format
    if (Array.isArray(data) && data.length > 0 && data[0].permission_key) {
      // We have database permissions format
      permissions.value = data
    } else if (typeof data === 'object' && !Array.isArray(data)) {
      // Fallback for backward compatibility - convert object to array
      permissions.value = Object.entries(data).map(([key, name]) => ({
        permission_key: key,
        name: name,
        description: '',
        category: key.split('.')[0] || 'general',
        level: key === '*' ? 4 : key.includes('admin') ? 3 : key.includes('advanced') ? 2 : 1,
        sort_order: 0
      }))
    } else {
      // Empty or invalid data
      permissions.value = []
    }
    
    // Group permissions by category
    permissionsByCategory.value = {}
    permissions.value.forEach(permission => {
      const category = permission.category || 'general'
      
      if (!permissionsByCategory.value[category]) {
        permissionsByCategory.value[category] = {
          name: category.charAt(0).toUpperCase() + category.slice(1).replace(/[_-]/g, ' '),
          permissions: [],
          metadata: categoryMetadata[category] || {
            icon: 'fa-cog',
            description: `${category} related permissions`,
            color: 'gray'
          }
        }
      }
      
      permissionsByCategory.value[category].permissions.push(permission)
    })
    
    // Sort permissions within each category by sort_order
    Object.values(permissionsByCategory.value).forEach(category => {
      category.permissions.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    })
    
  } catch (error) {
    console.error('Failed to fetch permissions:', error)
  } finally {
    loadingPermissions.value = false
  }
}

// Computed methods for user-specific permission display
const userHasPermission = (permissionKey) => {
  if (!selectedUser.value?.permissions) return false
  return selectedUser.value.permissions.includes('*') || selectedUser.value.permissions.includes(permissionKey)
}

const getTotalUserPermissions = () => {
  if (!selectedUser.value?.permissions) return 0
  if (selectedUser.value.permissions.includes('*')) return getTotalAvailablePermissions() + 1 // +1 for the * permission itself
  
  return selectedUser.value.permissions.length
}

const getTotalAvailablePermissions = () => {
  return permissions.value.length + 1 // +1 for the * permission
}

// Toggle permission function
const togglePermission = async (permission) => {
  if (!selectedUser.value || updatingPermissions.value) return
  
  updatingPermissions.value = true
  const currentPermissions = selectedUser.value.permissions || []
  let newPermissions = [...currentPermissions]
  
  // Handle special case for '*' (admin) permission
  if (permission === '*') {
    if (currentPermissions.includes('*')) {
      // Remove admin permission - keep only basic permissions
      newPermissions = ['view_fixtures', 'view_odds']
    } else {
      // Grant admin permission - replace all with '*'
      newPermissions = ['*']
    }
  } else {
    // Handle regular permissions
    if (currentPermissions.includes('*')) {
      // User currently has admin access
      // If trying to remove a permission, we need to break down admin to individual permissions
      const allPermissions = getAllAvailablePermissionsList()
      newPermissions = allPermissions.filter(p => p !== permission && p !== '*')
    } else {
      // Normal permission toggle
      if (currentPermissions.includes(permission)) {
        // Remove permission
        newPermissions = newPermissions.filter(p => p !== permission)
      } else {
        // Add permission
        newPermissions.push(permission)
      }
    }
  }
  
  try {
    // Update user permissions via API
    const response = await userStore.updateUser(selectedUser.value.id, {
      permissions: newPermissions
    })
    
    if (response) {
      // Update local user object
      selectedUser.value.permissions = newPermissions
      
      // Update the user in the store
      const userIndex = userStore.users.findIndex(u => u.id === selectedUser.value.id)
      if (userIndex !== -1) {
        userStore.users[userIndex].permissions = newPermissions
      }
      
      // Show success notification
      console.log('Permission updated successfully')
    }
  } catch (error) {
    console.error('Failed to update permission:', error)
    // You might want to show an error notification here
  } finally {
    updatingPermissions.value = false
  }
}

// Get all available permissions as a flat list
const getAllAvailablePermissionsList = () => {
  return permissions.value.map(permission => permission.permission_key)
}

// Quick action functions
const grantAllPermissions = async () => {
  if (!selectedUser.value) return
  
  try {
    const response = await userStore.updateUser(selectedUser.value.id, {
      permissions: ['*']
    })
    
    if (response) {
      selectedUser.value.permissions = ['*']
      const userIndex = userStore.users.findIndex(u => u.id === selectedUser.value.id)
      if (userIndex !== -1) {
        userStore.users[userIndex].permissions = ['*']
      }
      console.log('All permissions granted successfully')
    }
  } catch (error) {
    console.error('Failed to grant all permissions:', error)
  }
}

const removeAllPermissions = async () => {
  if (!selectedUser.value) return
  
  try {
    const response = await userStore.updateUser(selectedUser.value.id, {
      permissions: []
    })
    
    if (response) {
      selectedUser.value.permissions = []
      const userIndex = userStore.users.findIndex(u => u.id === selectedUser.value.id)
      if (userIndex !== -1) {
        userStore.users[userIndex].permissions = []
      }
      console.log('All permissions removed successfully')
    }
  } catch (error) {
    console.error('Failed to remove all permissions:', error)
  }
}

const setBasicPermissions = async () => {
  if (!selectedUser.value) return
  
  // Get basic permissions from the client role
  const clientRole = userStore.roles.find(r => r.id === 'client')
  const basicPermissions = clientRole ? clientRole.permissions : ['view_fixtures', 'view_odds']
  
  try {
    const response = await userStore.updateUser(selectedUser.value.id, {
      permissions: basicPermissions
    })
    
    if (response) {
      selectedUser.value.permissions = basicPermissions
      const userIndex = userStore.users.findIndex(u => u.id === selectedUser.value.id)
      if (userIndex !== -1) {
        userStore.users[userIndex].permissions = basicPermissions
      }
      console.log('Basic permissions set successfully')
    }
  } catch (error) {
    console.error('Failed to set basic permissions:', error)
  }
}

// Permission usage modal functions
const showUsersWithPermission = (permissionKey) => {
  selectedPermissionKey.value = permissionKey
  
  // Find all users who have this permission
  const usersWithPermission = userStore.users.filter(user => {
    if (!user.permissions || !Array.isArray(user.permissions)) return false
    
    // Check if user has the specific permission or has wildcard access
    return user.permissions.includes(permissionKey) || user.permissions.includes('*')
  })
  
  usersWithSelectedPermission.value = usersWithPermission
  showPermissionUsersModal.value = true
}

const closePermissionUsersModal = () => {
  showPermissionUsersModal.value = false
  selectedPermissionKey.value = ''
  usersWithSelectedPermission.value = []
}

// Update user role function
const updateUserRole = async (newRole) => {
  if (!selectedUser.value) return
  
  try {
    // Get default permissions for the new role
    const role = userStore.roles.find(r => r.id === newRole)
    const defaultPermissions = role ? role.permissions : []
    
    const response = await userStore.updateUser(selectedUser.value.id, {
      role: newRole,
      permissions: defaultPermissions
    })
    
    if (response) {
      // Update local user object
      selectedUser.value.role = newRole
      selectedUser.value.permissions = defaultPermissions
      
      // Update the user in the store
      const userIndex = userStore.users.findIndex(u => u.id === selectedUser.value.id)
      if (userIndex !== -1) {
        userStore.users[userIndex].role = newRole
        userStore.users[userIndex].permissions = defaultPermissions
      }
      
      console.log('User role updated successfully')
    }
  } catch (error) {
    console.error('Failed to update user role:', error)
  }
}

// Add new permission functions
const openAddPermissionModal = () => {
  // Reset form
  newPermission.value = {
    permission_key: '',
    name: '',
    description: '',
    category: 'general',
    level: 1,
    is_active: true,
    is_system: false,
    sort_order: 0
  }
  showAddPermissionModal.value = true
}

const closeAddPermissionModal = () => {
  showAddPermissionModal.value = false
  newPermission.value = {
    permission_key: '',
    name: '',
    description: '',
    category: 'general',
    level: 1,
    is_active: true,
    is_system: false,
    sort_order: 0
  }
}

const createPermission = async () => {
  if (!newPermission.value.permission_key || !newPermission.value.name) {
    alert('Permission key and name are required')
    return
  }
  
  addingPermission.value = true
  
  try {
    // Make API call to create permission
    const response = await axios.post('/api/permissions', {
      permission_key: newPermission.value.permission_key,
      name: newPermission.value.name,
      description: newPermission.value.description,
      category: newPermission.value.category,
      level: newPermission.value.level,
      is_active: newPermission.value.is_active,
      is_system: newPermission.value.is_system,
      sort_order: newPermission.value.sort_order || 0
    })
    
    if (response.data) {
      // Add to local permissions array
      permissions.value.push(response.data)
      
      // Update permissions by category
      await fetchPermissions()
      
      console.log('Permission created successfully')
      closeAddPermissionModal()
    }
  } catch (error) {
    console.error('Failed to create permission:', error)
    alert('Failed to create permission: ' + (error.response?.data?.message || error.message))
  } finally {
    addingPermission.value = false
  }
}

// Get available categories from existing permissions
const getAvailableCategories = () => {
  const categories = [...new Set(permissions.value.map(p => p.category))]
  const defaultCategories = ['users', 'trading', 'markets', 'fixtures', 'teams', 'players', 'rosters', 'reports', 'analytics', 'exports', 'admin', 'navigation', 'general']
  
  // Combine and remove duplicates
  return [...new Set([...defaultCategories, ...categories])].sort()
}

// API Token Management
const selectedUserForTokens = ref(null)

// Generate Token Modal (standalone - not used by ApiTokenManager)
const showGenerateTokenModal = ref(false)
const newTokenName = ref('')
const newTokenAbilities = ref(['*'])
const generatingToken = ref(false)
const availableTokenAbilities = [
  { key: '*', name: 'All Permissions', description: 'Full access to all API endpoints' },
  { key: 'fixtures.read', name: 'Read Fixtures', description: 'View fixture data and information' },
  { key: 'fixtures.write', name: 'Write Fixtures', description: 'Create and modify fixtures' },
  { key: 'markets.read', name: 'Read Markets', description: 'View market data and odds' },
  { key: 'markets.write', name: 'Write Markets', description: 'Create and modify markets' },
  { key: 'players.read', name: 'Read Players', description: 'View player data and statistics' },
  { key: 'players.write', name: 'Write Players', description: 'Create and modify player data' },
  { key: 'teams.read', name: 'Read Teams', description: 'View team information' },
  { key: 'teams.write', name: 'Write Teams', description: 'Create and modify team data' },
  { key: 'reports.read', name: 'Read Reports', description: 'Access to reporting data' },
  { key: 'analytics.read', name: 'Read Analytics', description: 'Access to analytics data' },
  { key: 'users.read', name: 'Read Users', description: 'View user information (limited)' },
  { key: 'api.read', name: 'API Read Only', description: 'Read-only access to all API endpoints' }
]

// Token Details Modal
const showTokenDetailsModal = ref(false)
const selectedToken = ref(null)
const adminPasswordForToken = ref('')
const tokenAccessError = ref('')
const verifyingTokenAccess = ref(false)

const generateNewToken = async () => {
  if (!newTokenName.value?.trim() || !selectedUserForTokens.value) return
  generatingToken.value = true
  try {
    const response = await axios.post(`/api/users/${selectedUserForTokens.value.id}/tokens`, {
      name: newTokenName.value,
      abilities: newTokenAbilities.value
    })
    if (response.data) {
      alert(`Token generated!\n\nToken: ${response.data.token}\n\nCopy it now - it won't be shown again.`)
      newTokenName.value = ''
      newTokenAbilities.value = ['*']
      showGenerateTokenModal.value = false
      handleTokenGenerated(response.data)
    }
  } catch (err) {
    console.error('Failed to generate token:', err)
    alert('Failed to generate token: ' + (err.response?.data?.message || err.message))
  } finally {
    generatingToken.value = false
  }
}

const closeTokenDetailsModal = () => {
  showTokenDetailsModal.value = false
  selectedToken.value = null
  adminPasswordForToken.value = ''
  tokenAccessError.value = ''
}

const verifyTokenAccess = async () => {
  if (!adminPasswordForToken.value) {
    tokenAccessError.value = 'Password is required'
    return
  }
  verifyingTokenAccess.value = true
  try {
    const response = await axios.post('/api/verify-admin-access', { password: adminPasswordForToken.value })
    tokenAccessError.value = response.data?.verified ? '' : 'Invalid password'
  } catch {
    tokenAccessError.value = 'Verification failed'
  } finally {
    verifyingTokenAccess.value = false
  }
}

const copyTokenToClipboard = async (token) => {
  try {
    await navigator.clipboard.writeText(token)
  } catch (err) {
    console.error('Failed to copy token:', err)
  }
}

const getTokenStatus = (token) => {
  if (!token) return { status: 'Unknown', class: 'bg-gray-100 text-gray-800' }
  if (token.expires_at && new Date(token.expires_at) < new Date()) return { status: 'Expired', class: 'bg-red-100 text-red-800' }
  return { status: 'Active', class: 'bg-green-100 text-green-800' }
}

const formatLastUsed = (date) => {
  if (!date) return 'Never'
  return new Date(date).toLocaleString()
}

// Event handlers for token management
const handleTokenGenerated = (token) => {
  console.log('Token generated:', token)
  // Refresh user tokens if needed
  if (selectedUserForTokens.value) {
    // The ApiTokenManager will handle the refresh
  }
}

const handleTokenRevoked = (tokenId) => {
  console.log('Token revoked:', tokenId)
  // The ApiTokenManager will handle the refresh
}

// Password change functions
const openPasswordChangeModal = () => {
  passwordChangeForm.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  }
  passwordChangeError.value = ''
  passwordChangeSuccess.value = false
  showPasswordChangeModal.value = true
}

const closePasswordChangeModal = () => {
  showPasswordChangeModal.value = false
  passwordChangeForm.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  }
  passwordChangeError.value = ''
  passwordChangeSuccess.value = false
}

const validatePasswordForm = () => {
  const { currentPassword, newPassword, confirmPassword } = passwordChangeForm.value
  
  if (!currentPassword.trim()) {
    passwordChangeError.value = 'Current password is required'
    return false
  }
  
  if (!newPassword.trim()) {
    passwordChangeError.value = 'New password is required'
    return false
  }
  
  if (newPassword.length < 8) {
    passwordChangeError.value = 'New password must be at least 8 characters long'
    return false
  }
  
  if (newPassword !== confirmPassword) {
    passwordChangeError.value = 'New passwords do not match'
    return false
  }
  
  if (newPassword === currentPassword) {
    passwordChangeError.value = 'New password must be different from current password'
    return false
  }
  
  passwordChangeError.value = ''
  return true
}

const updateUserPassword = async () => {
  if (!validatePasswordForm()) return
  
  changingPassword.value = true
  passwordChangeError.value = ''
  
  try {
    // Update user password via the dedicated password endpoint
    const response = await axios.put(`/api/users/${selectedUser.value.id}/password`, {
      current_password: passwordChangeForm.value.currentPassword,
      password: passwordChangeForm.value.newPassword,
      password_confirmation: passwordChangeForm.value.confirmPassword
    })
    
    if (response.data) {
      passwordChangeSuccess.value = true
      passwordChangeForm.value = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      
      // Show success message for a few seconds then close
      setTimeout(() => {
        closePasswordChangeModal()
      }, 2000)
      
      console.log('Password updated successfully')
    }
  } catch (error) {
    console.error('Failed to update password:', error)
    passwordChangeError.value = error.response?.data?.message || 'Failed to update password. Please try again.'
  } finally {
    changingPassword.value = false
  }
}

// Fetch available leagues
const fetchLeagues = async () => {
  loadingLeagues.value = true
  try {
    const response = await axios.get('/api/leagues')
    availableLeagues.value = response.data || []
  } catch (error) {
    console.error('Failed to fetch leagues:', error)
    availableLeagues.value = []
  } finally {
    loadingLeagues.value = false
  }
}

// Check if user has access to a league
const userHasLeagueAccess = (leagueId) => {
  if (!selectedUser.value?.league_access) return false
  return selectedUser.value.league_access.includes(leagueId)
}

// Toggle league access for user
const toggleLeagueAccess = async (leagueId) => {
  if (!selectedUser.value || updatingLeagueAccess.value) return
  
  updatingLeagueAccess.value = true
  const currentAccess = selectedUser.value.league_access || []
  let newAccess = [...currentAccess]
  
  if (currentAccess.includes(leagueId)) {
    // Remove league access
    newAccess = newAccess.filter(id => id !== leagueId)
  } else {
    // Add league access
    newAccess.push(leagueId)
  }
  
  try {
    // Update user league access via API
    const response = await userStore.updateUser(selectedUser.value.id, {
      league_access: newAccess
    })
    
    if (response) {
      // Update local user object
      selectedUser.value.league_access = newAccess
      
      // Update the user in the store
      const userIndex = userStore.users.findIndex(u => u.id === selectedUser.value.id)
      if (userIndex !== -1) {
        userStore.users[userIndex].league_access = newAccess
      }
      
      console.log('League access updated successfully')
    }
  } catch (error) {
    console.error('Failed to update league access:', error)
  } finally {
    updatingLeagueAccess.value = false
  }
}

// Grant access to all leagues
const grantAllLeagues = async () => {
  if (!selectedUser.value) return
  
  const allLeagueIds = availableLeagues.value.map(league => league.id)
  
  try {
    const response = await userStore.updateUser(selectedUser.value.id, {
      league_access: allLeagueIds
    })
    
    if (response) {
      selectedUser.value.league_access = allLeagueIds
      const userIndex = userStore.users.findIndex(u => u.id === selectedUser.value.id)
      if (userIndex !== -1) {
        userStore.users[userIndex].league_access = allLeagueIds
      }
      console.log('All leagues granted successfully')
    }
  } catch (error) {
    console.error('Failed to grant all leagues:', error)
  }
}

// Remove access to all leagues
const removeAllLeagues = async () => {
  if (!selectedUser.value) return
  
  try {
    const response = await userStore.updateUser(selectedUser.value.id, {
      league_access: []
    })
    
    if (response) {
      selectedUser.value.league_access = []
      const userIndex = userStore.users.findIndex(u => u.id === selectedUser.value.id)
      if (userIndex !== -1) {
        userStore.users[userIndex].league_access = []
      }
      console.log('All leagues removed successfully')
    }
  } catch (error) {
    console.error('Failed to remove all leagues:', error)
  }
}
</script>

<template>
  <div v-if="show" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-8"
       @click.self="$emit('close')"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b">
          <h2 class="text-2xl font-semibold text-gray-800">User Management</h2>
          <button @click="$emit('close')" 
                  class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Tabs -->
        <div class="border-b px-6">
          <div class="flex space-x-6">
            <button 
              v-for="tab in ['Users', 'Permissions', 'Roles', 'Packages', 'API Tokens']" 
              :key="tab"
              @click="activeTab = tab.toLowerCase().replace(' ', '-')"
              :class="[
                'px-4 py-3 font-medium border-b-2 transition-colors',
                activeTab === tab.toLowerCase().replace(' ', '-')
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              ]"
            >
              {{ tab }}
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6 flex-1 overflow-y-auto">
          <!-- Users Tab -->
          <div v-if="activeTab === 'users'" class="space-y-6">
            <div class="flex justify-between items-center">
              <div class="flex gap-4">
                <div class="relative">
                  <input 
                    v-model="searchQuery"
                    type="text" 
                    placeholder="Search users..."
                    class="pl-10 pr-4 py-2 border rounded-lg"
                  >
                  <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
                </div>
                <select 
                  v-model="selectedRole"
                  class="border rounded-lg px-4 py-2"
                >
                  <option value="">All Roles</option>
                  <option v-for="role in (userStore.roles || [])" :key="role.id" :value="role.id">
                    {{ role.name }}
                  </option>
                </select>
                <select 
                  v-model="selectedOrganization"
                  class="border rounded-lg px-4 py-2"
                >
                  <option value="">All Organizations</option>
                  <option v-for="org in availableOrganizations" :key="org" :value="org">
                    {{ org }}
                  </option>
                </select>
              </div>
              <button class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" @click="showAddUserForm = true">
                <i class="fas fa-plus mr-2"></i>
                Add User
              </button>
            </div>

            <!-- Loading State -->
            <div v-if="userStore.loading" class="flex justify-center items-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>

            <!-- Error State -->
            <div v-else-if="userStore.error" class="bg-red-50 border border-red-200 rounded-lg p-4">
              <div class="flex items-center text-red-800">
                <i class="fas fa-exclamation-circle mr-2"></i>
                <span>{{ userStore.error }}</span>
              </div>
            </div>

            <!-- Users by Organization -->
            <div v-else class="space-y-4">
              <div v-for="org in usersByOrganization" :key="org.name" class="bg-white rounded-lg border">
                <!-- Organization Header -->
                <div class="bg-gray-50 px-6 py-4 border-b flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <i class="fas fa-building text-blue-600"></i>
                    </div>
                    <div>
                      <h3 class="font-semibold text-gray-900">{{ org.name }}</h3>
                      <p class="text-sm text-gray-600">{{ org.count }} {{ org.count === 1 ? 'member' : 'members' }}</p>
                    </div>
                  </div>
                  <button 
                    @click="viewOrganizationTeam(org.name)"
                    class="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <i class="fas fa-users"></i>
                    View Team
                  </button>
                </div>

                <!-- Organization Users -->
                <div class="divide-y divide-gray-200">
                  <div v-for="user in org.users" 
                       :key="user.id" 
                       class="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                       @click="viewUserDetails(user)"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-4">
                        <div class="h-10 w-10 flex-shrink-0">
                          <div class="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span class="text-gray-600 font-medium">
                              {{ user.name ? user.name.charAt(0) : (user.email ? user.email.charAt(0) : '?') }}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div class="font-medium text-gray-900">{{ user.name }}</div>
                          <div class="text-sm text-gray-500">{{ user.email }}</div>
                        </div>
                      </div>
                      
                      <div class="flex items-center gap-3">
                        <span :class="{
                          'px-2 py-1 text-xs rounded-full': true,
                          'bg-yellow-100 text-yellow-800': user.role === 'admin',
                          'bg-green-100 text-green-800': user.role === 'trader',
                          'bg-blue-100 text-blue-800': user.role === 'client'
                        }">
                          {{ user.role || 'N/A' }}
                        </span>
                        <span :class="{
                          'px-2 py-1 text-xs rounded-full': true,
                          'bg-green-100 text-green-800': user.status === 'active',
                          'bg-red-100 text-red-800': user.status === 'inactive',
                          'bg-yellow-100 text-yellow-800': user.status === 'pending'
                        }">
                          {{ user.status || 'N/A' }}
                        </span>
                        <div class="text-sm text-gray-500">
                          {{ user.lastActive ? new Date(user.lastActive).toLocaleString() : 'Never' }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No users message -->
              <div v-if="usersByOrganization.length === 0" class="text-center py-8 bg-gray-50 rounded-lg">
                <div class="text-gray-400 mb-4">
                  <i class="fas fa-users text-4xl"></i>
                </div>
                <div class="text-gray-600">No users found</div>
                <div class="text-sm text-gray-500 mt-2">
                  Try adjusting your search or filter criteria
                </div>
              </div>
            </div>
          </div>

          <!-- Permissions Tab -->
          <div v-if="activeTab === 'permissions'" class="space-y-6">
            <!-- Simple Header -->
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">All System Permissions</h3>
                <p class="text-sm text-gray-600">Manage permissions available in the system</p>
              </div>
              
              <div class="flex items-center gap-4">
                <!-- Stats -->
                <div class="flex gap-4 text-center">
                  <div>
                    <div class="text-xl font-bold text-gray-900">{{ permissions.length }}</div>
                    <div class="text-xs text-gray-600">Total</div>
                  </div>
                  <div>
                    <div class="text-xl font-bold text-blue-600">{{ Object.keys(permissionsByCategory).length }}</div>
                    <div class="text-xs text-gray-600">Categories</div>
                  </div>
                </div>
                
                <!-- Add Permission Button -->
                <button 
                  @click="openAddPermissionModal"
                  class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <i class="fas fa-plus"></i>
                  Add Permission
                </button>
              </div>
            </div>

            <!-- Search Filter -->
            <div class="flex items-center gap-4">
              <div class="relative flex-1 max-w-md">
                <input 
                  v-model="permissionsSearchQuery"
                  type="text" 
                  placeholder="Search permissions by name, key, category, or description..."
                  class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
              </div>
              <div v-if="permissionsSearchQuery" class="text-sm text-gray-600">
                {{ filteredPermissions.length }} of {{ permissions.length }} permissions
              </div>
            </div>

            <!-- Loading state -->
            <div v-if="loadingPermissions" class="flex justify-center items-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>

            <!-- Permissions Table -->
            <div v-else class="bg-white border rounded-lg overflow-hidden">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permission</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <!-- Super Admin Permission -->
                  <tr v-if="!permissionsSearchQuery || 
                           (permissionsSearchQuery.toLowerCase() && (
                             'all permissions super admin'.includes(permissionsSearchQuery.toLowerCase()) ||
                             permissionsSearchQuery.toLowerCase().includes('*') ||
                             permissionsSearchQuery.toLowerCase().includes('super') ||
                             permissionsSearchQuery.toLowerCase().includes('admin') ||
                             permissionsSearchQuery.toLowerCase().includes('special') ||
                             permissionsSearchQuery.toLowerCase().includes('all') ||
                             'grants unrestricted access to all system features and functionality'.includes(permissionsSearchQuery.toLowerCase())
                           ))"
                      class="bg-yellow-50">
                    <td class="px-4 py-3">
                      <div class="flex items-center gap-2">
                        <i class="fas fa-crown text-yellow-600"></i>
                        <span class="font-medium text-gray-900">All Permissions (Super Admin)</span>
                      </div>
                    </td>
                    <td class="px-4 py-3">
                      <code class="text-sm bg-gray-100 px-2 py-1 rounded cursor-pointer hover:bg-blue-100 transition-colors" 
                            @click="showUsersWithPermission('*')"
                            title="Click to see users with this permission">
                        *
                      </code>
                    </td>
                    <td class="px-4 py-3">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Special
                      </span>
                    </td>
                    <td class="px-4 py-3">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Super Admin
                      </span>
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-600">
                      Grants unrestricted access to all system features and functionality
                    </td>
                  </tr>
                  
                  <!-- Regular Permissions -->
                  <tr v-for="permission in filteredPermissions" :key="permission.permission_key" class="hover:bg-gray-50">
                    <td class="px-4 py-3">
                      <span class="text-sm font-medium text-gray-900">{{ permission.name }}</span>
                    </td>
                    <td class="px-4 py-3">
                      <code class="text-sm bg-gray-100 px-2 py-1 rounded cursor-pointer hover:bg-blue-100 transition-colors" 
                            @click="showUsersWithPermission(permission.permission_key)"
                            title="Click to see users with this permission">
                        {{ permission.permission_key }}
                      </code>
                    </td>
                    <td class="px-4 py-3">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            :class="{
                              'bg-blue-100 text-blue-800': permission.category === 'users',
                              'bg-green-100 text-green-800': permission.category === 'trading',
                              'bg-purple-100 text-purple-800': permission.category === 'markets',
                              'bg-orange-100 text-orange-800': permission.category === 'fixtures',
                              'bg-teal-100 text-teal-800': permission.category === 'teams',
                              'bg-indigo-100 text-indigo-800': permission.category === 'players',
                              'bg-cyan-100 text-cyan-800': permission.category === 'rosters',
                              'bg-red-100 text-red-800': permission.category === 'reports',
                              'bg-pink-100 text-pink-800': permission.category === 'analytics',
                              'bg-gray-100 text-gray-800': permission.category === 'exports',
                              'bg-yellow-100 text-yellow-800': permission.category === 'admin',
                              'bg-emerald-100 text-emerald-800': permission.category === 'navigation',
                              'bg-gray-100 text-gray-800': !['users', 'trading', 'markets', 'fixtures', 'teams', 'players', 'rosters', 'reports', 'analytics', 'exports', 'admin', 'navigation'].includes(permission.category)
                            }">
                        {{ permission.category }}
                      </span>
                    </td>
                    <td class="px-4 py-3">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            :class="{
                              'bg-green-100 text-green-800': permission.level === 1,
                              'bg-yellow-100 text-yellow-800': permission.level === 2,
                              'bg-red-100 text-red-800': permission.level === 3,
                              'bg-purple-100 text-purple-800': permission.level === 4
                            }">
                        {{ permission.level === 1 ? 'Basic' : permission.level === 2 ? 'Advanced' : permission.level === 3 ? 'Admin' : 'Super Admin' }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-600">
                      {{ permission.description || 'No description available' }}
                    </td>
                  </tr>
                  
                  <!-- No results message -->
                  <tr v-if="filteredPermissions.length === 0 && permissionsSearchQuery && 
                           !(permissionsSearchQuery.toLowerCase() && (
                             'all permissions super admin'.includes(permissionsSearchQuery.toLowerCase()) ||
                             permissionsSearchQuery.toLowerCase().includes('*') ||
                             permissionsSearchQuery.toLowerCase().includes('super') ||
                             permissionsSearchQuery.toLowerCase().includes('admin') ||
                             permissionsSearchQuery.toLowerCase().includes('special') ||
                             permissionsSearchQuery.toLowerCase().includes('all') ||
                             'grants unrestricted access to all system features and functionality'.includes(permissionsSearchQuery.toLowerCase())
                           ))">
                    <td colspan="5" class="px-4 py-8 text-center text-gray-500">
                      <div class="flex flex-col items-center gap-2">
                        <i class="fas fa-search text-gray-400 text-2xl"></i>
                        <div>No permissions found matching "{{ permissionsSearchQuery }}"</div>
                        <button @click="permissionsSearchQuery = ''" class="text-blue-600 hover:text-blue-700 text-sm">
                          Clear search
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Packages Tab -->
          <div v-if="activeTab === 'packages'" class="space-y-6">
            <div class="grid grid-cols-2 gap-6">
              <div v-for="pkg in (userStore.packages || [])" :key="pkg.id"
                   class="border rounded-lg p-6 space-y-4 relative overflow-hidden"
              >
                <!-- Package Header -->
                <div class="text-xl font-semibold">{{ pkg.name }}</div>
                
                <!-- Price Section -->
                <div class="flex items-baseline gap-2">
                  <div class="text-3xl font-bold">${{ pkg.price }}</div>
                  <div class="text-gray-500">
                    {{ pkg.id === 'monthly' ? '/month' : '/game' }}
                  </div>
                </div>

                <!-- Games Info -->
                <div class="bg-blue-50 p-4 rounded-lg">
                  <div class="font-medium text-blue-800">
                    {{ pkg.id === 'monthly' 
                      ? `${pkg.gamesIncluded} games included per month` 
                      : 'Pay per game access' }}
                  </div>
                  <div class="text-sm text-blue-600 mt-1">
                    {{ pkg.id === 'monthly' 
                      ? `Additional games: €${pkg.extraGamePrice}/game` 
                      : 'No minimum commitment' }}
                  </div>
                </div>

                <!-- Features List -->
                <div class="space-y-3">
                  <div class="font-medium text-gray-700">Includes:</div>
                  <ul class="space-y-2">
                    <li v-for="feature in pkg.features" :key="feature"
                        class="flex items-start gap-2 text-sm">
                      <i class="fas fa-check text-green-500 mt-1"></i>
                      <span>{{ feature }}</span>
                    </li>
                  </ul>
                </div>

                <!-- League Access -->
                <div class="space-y-3">
                  <div class="font-medium text-gray-700">Available Leagues:</div>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="league in pkg.leagueAccess" :key="league"
                          class="px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {{ league.toUpperCase() }}
                    </span>
                  </div>
                </div>

                <!-- API Info -->
                <div class="mt-4 pt-4 border-t">
                  <div class="text-sm text-gray-600">
                    API Limit: {{ pkg.apiLimit.toLocaleString() }} requests/month
                  </div>
                </div>

                <!-- Action Button -->
                <button class="w-full mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Select Plan
                </button>
              </div>
            </div>

            <!-- Additional Information -->
            <div class="mt-8 p-4 bg-gray-50 rounded-lg">
              <h3 class="font-medium text-gray-800 mb-2">Additional Information</h3>
              <ul class="space-y-2 text-sm text-gray-600">
                <li class="flex items-start gap-2">
                  <i class="fas fa-info-circle mt-1"></i>
                  <span>Monthly package includes 120 games with option to purchase additional games at €12 each</span>
                </li>
                <li class="flex items-start gap-2">
                  <i class="fas fa-info-circle mt-1"></i>
                  <span>All packages include access to basic API features and real-time updates</span>
                </li>
                <li class="flex items-start gap-2">
                  <i class="fas fa-info-circle mt-1"></i>
                  <span>Unused games from monthly package do not roll over to next month</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Roles Tab -->
          <div v-if="activeTab === 'roles'" class="space-y-6">
            <div class="grid grid-cols-3 gap-6">
              <div v-for="role in userStore.roles" :key="role.id"
                   class="border rounded-lg p-6">
                <div class="text-xl font-semibold mb-4">{{ role.name }}</div>
                <div class="space-y-2">
                  <div class="text-sm font-medium text-gray-500">Permissions:</div>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="perm in role.permissions" :key="perm"
                          class="px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {{ perm }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- API Tokens Tab -->
          <div v-if="activeTab === 'api-tokens'" class="space-y-6">
            <!-- User Selection for Token Management -->
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <i class="fas fa-user text-blue-600"></i>
                </div>
                <div>
                  <h4 class="font-medium text-blue-900">Select User for Token Management</h4>
                  <p class="text-sm text-blue-700">Choose a user to view and manage their API tokens</p>
                </div>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div 
                  v-for="user in userStore.users" 
                  :key="user.id"
                  class="border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md"
                  :class="{
                    'border-blue-500 bg-blue-50': selectedUserForTokens?.id === user.id,
                    'border-gray-200': selectedUserForTokens?.id !== user.id
                  }"
                  @click="selectedUserForTokens = user"
                >
                  <div class="flex items-center gap-3">
                    <div class="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                      <span class="text-gray-600 font-medium text-sm">
                        {{ user.name ? user.name.charAt(0) : (user.email ? user.email.charAt(0) : '?') }}
                      </span>
                    </div>
                    <div>
                      <div class="font-medium text-sm">{{ user.name || 'Unknown User' }}</div>
                      <div class="text-xs text-gray-500">{{ user.email }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Token Management Panel -->
            <ApiTokenManager 
              v-if="selectedUserForTokens"
              :key="selectedUserForTokens?.id"
              :user="selectedUserForTokens"
              :user-store="userStore"
              @token-generated="handleTokenGenerated"
              @token-revoked="handleTokenRevoked"
            />

            <!-- No User Selected State -->
            <div v-else class="text-center py-8 bg-gray-50 rounded-lg">
              <div class="text-gray-400 mb-4">
                <i class="fas fa-users text-4xl"></i>
              </div>
              <div class="text-gray-600 mb-2">Select a user to manage tokens</div>
              <div class="text-sm text-gray-500">
                Choose a user from the list above to view and manage their API tokens
              </div>
            </div>
          </div>
        </div>
    </div>
  </div>

  <!-- Add user details overlay -->
  <div v-if="showUserDetails" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-start justify-center overflow-y-auto"
       @click.self="closeUserDetails"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full m-8">
      <!-- Header -->
      <div class="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl">
        <div class="flex items-center gap-4">
      
          <h2 class="text-xl font-semibold">User Details</h2>
        </div>
        <div class="flex items-center gap-2">
          <span :class="{
            'px-2 py-1 text-xs rounded-full': true,
            'bg-green-100 text-green-800': selectedUser?.status === 'active',
            'bg-red-100 text-red-800': selectedUser?.status === 'inactive'
          }">
            {{ selectedUser?.status }}
            
          </span>
          <button @click="closeUserDetails" class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Content - Keep the same content, just update the wrapper -->
      <div class="p-6 space-y-8 max-h-[calc(100vh-12rem)] overflow-y-auto">
        <!-- User Info -->
        <div class="flex items-start gap-6">
          <div class="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
            <span class="text-2xl text-gray-600 font-medium">
              {{ selectedUser?.name ? selectedUser?.name.charAt(0) : (selectedUser?.email ? selectedUser?.email.charAt(0) : '?') }}
            </span>
          </div>
          <div>
            <h3 class="text-xl font-medium">{{ selectedUser?.name }}</h3>
            <p class="text-gray-500">{{ selectedUser?.email }}</p>
            <div class="mt-2 flex items-center gap-2">
              <span :class="{
                'px-2 py-1 rounded-full text-xs': true,
                'bg-yellow-100 text-yellow-800': selectedUser?.role === 'admin',
                'bg-green-100 text-green-800': selectedUser?.role === 'trader',
                'bg-blue-100 text-blue-800': selectedUser?.role === 'client'
              }">
                {{ selectedUser?.role }}
              </span>
              <span :class="{
                'px-2 py-1 rounded-full text-xs': true,
                'bg-green-100 text-green-800': selectedUser?.status === 'active',
                'bg-red-100 text-red-800': selectedUser?.status === 'inactive',
                'bg-yellow-100 text-yellow-800': selectedUser?.status === 'pending'
              }">
                {{ selectedUser?.status }}
              </span>
              <span class="text-sm text-gray-500">
                Last active: {{ new Date(selectedUser?.lastActive).toLocaleString() }}
              </span>
            </div>
          </div>
        </div>

        <!-- Password Management -->
        <div class="border-t pt-8">
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <i class="fas fa-lock text-red-600"></i>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Password Management</h3>
                <p class="text-sm text-gray-600">Change the user's password securely</p>
              </div>
            </div>
            
            <button 
              @click="openPasswordChangeModal"
              class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              <i class="fas fa-key"></i>
              Change Password
            </button>
          </div>
          
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div class="flex items-start gap-3">
              <i class="fas fa-exclamation-triangle text-yellow-600 mt-1"></i>
              <div>
                <h4 class="font-medium text-yellow-900">Security Notice</h4>
                <p class="text-sm text-yellow-700 mt-1">
                  Changing a user's password will require them to use the new password on their next login. 
                  Make sure to communicate the new password securely to the user.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Role Management -->
        <div class="border-t pt-8">
          <div class="flex items-center gap-3 mb-6">
            <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <i class="fas fa-user-tag text-blue-600"></i>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-gray-900">User Role</h3>
              <p class="text-sm text-gray-600">Change the user's role to apply default permissions</p>
            </div>
          </div>
          
          <div class="grid grid-cols-3 gap-4 mb-8">
            <div 
              v-for="role in userStore.roles" 
              :key="role.id"
              class="border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md"
              :class="{
                'border-blue-500 bg-blue-50': selectedUser?.role === role.id,
                'border-gray-200': selectedUser?.role !== role.id
              }"
              @click="updateUserRole(role.id)"
            >
              <div class="flex items-center justify-between mb-2">
                <h5 class="font-medium">{{ role.name }}</h5>
                <i 
                  :class="role.id === 'admin' ? 'fas fa-crown text-yellow-500' : 
                          role.id === 'trader' ? 'fas fa-chart-line text-green-500' : 
                          'fas fa-user text-blue-500'"
                ></i>
              </div>
              <div class="text-sm text-gray-600 mb-3">
                Default: {{ role.permissions.includes('*') ? 'All' : role.permissions.length }} permissions
              </div>
              <div class="space-y-1">
                <div 
                  v-for="permission in role.permissions.slice(0, 2)" 
                  :key="permission"
                  class="text-xs bg-gray-100 rounded px-2 py-1"
                >
                  {{ permission === '*' ? 'Full Access' : permission.replace('_', ' ') }}
                </div>
                <div v-if="role.permissions.length > 2" class="text-xs text-gray-500">
                  +{{ role.permissions.length - 2 }} more...
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- User's Permissions Management -->
        <div class="border-t pt-8">
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <i class="fas fa-list-check text-indigo-600"></i>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Individual Permissions</h3>
                <p class="text-sm text-gray-600">Click any permission to toggle it on/off</p>
              </div>
            </div>
            
            <!-- Quick Actions -->
            <div class="flex items-center gap-2">
              <button 
                @click="grantAllPermissions"
                class="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
              >
                <i class="fas fa-check-double"></i>
                Grant All
              </button>
              <button 
                @click="removeAllPermissions"
                class="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center gap-2"
              >
                <i class="fas fa-times-circle"></i>
                Remove All
              </button>
              <button 
                @click="setBasicPermissions"
                class="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-2"
              >
                <i class="fas fa-user"></i>
                Client Role
              </button>
          </div>
        </div>

          <!-- Search Filter for User Permissions -->
          <div class="flex items-center gap-4 mb-4">
            <div class="relative flex-1 max-w-md">
              <input 
                v-model="userPermissionsSearchQuery"
                type="text" 
                placeholder="Search user's permissions..."
                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
              <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
            </div>
            <div v-if="userPermissionsSearchQuery" class="text-sm text-gray-600">
              {{ filteredUserPermissions.length + 1 }} of {{ permissions.length + 1 }} permissions
            </div>
          </div>

          <!-- User Permissions Table -->
          <div v-if="!loadingPermissions" class="bg-white border rounded-lg overflow-hidden">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permission</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <!-- Super Admin Permission -->
                <tr v-if="!userPermissionsSearchQuery || 
                         (userPermissionsSearchQuery.toLowerCase() && (
                           'all permissions super admin'.includes(userPermissionsSearchQuery.toLowerCase()) ||
                           userPermissionsSearchQuery.toLowerCase().includes('*') ||
                           userPermissionsSearchQuery.toLowerCase().includes('super') ||
                           userPermissionsSearchQuery.toLowerCase().includes('admin') ||
                           userPermissionsSearchQuery.toLowerCase().includes('special') ||
                           userPermissionsSearchQuery.toLowerCase().includes('all') ||
                           'grants unrestricted access to all system features and functionality'.includes(userPermissionsSearchQuery.toLowerCase())
                         ))"
                    class="bg-yellow-50 cursor-pointer hover:bg-yellow-100 transition-colors" 
                    @click="togglePermission('*')">
                  <td class="px-4 py-3">
                    <div class="flex items-center">
                      <div 
                        class="w-6 h-6 rounded flex items-center justify-center transition-all duration-200"
                        :class="userHasPermission('*') ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-300 hover:bg-gray-400'"
                      >
                        <i v-if="userHasPermission('*')" class="fas fa-check text-white text-sm"></i>
                        <i v-else class="fas fa-times text-white text-sm"></i>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-2">
                      <i class="fas fa-crown text-yellow-600"></i>
                      <span class="font-medium text-gray-900">All Permissions (Super Admin)</span>
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <code class="text-sm bg-gray-100 px-2 py-1 rounded">*</code>
                  </td>
                  <td class="px-4 py-3">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Special
                    </span>
                  </td>
                  <td class="px-4 py-3">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Super Admin
                    </span>
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600">
                    Grants unrestricted access to all system features and functionality
                  </td>
                </tr>
                
                <!-- Regular Permissions -->
                <tr v-for="permission in filteredUserPermissions" 
                    :key="permission.permission_key" 
                    class="cursor-pointer transition-colors"
                    :class="userHasPermission(permission.permission_key) ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-50'"
                    @click="togglePermission(permission.permission_key)">
                  <td class="px-4 py-3">
                    <div class="flex items-center">
                      <div 
                        class="w-6 h-6 rounded flex items-center justify-center transition-all duration-200"
                        :class="userHasPermission(permission.permission_key) ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-300 hover:bg-gray-400'"
                      >
                        <i v-if="userHasPermission(permission.permission_key)" class="fas fa-check text-white text-sm"></i>
                        <i v-else class="fas fa-times text-white text-sm"></i>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <span class="text-sm font-medium" 
                          :class="userHasPermission(permission.permission_key) ? 'text-green-900' : 'text-gray-900'">
                      {{ permission.name }}
                    </span>
                  </td>
                  <td class="px-4 py-3">
                    <code class="text-sm bg-gray-100 px-2 py-1 rounded">{{ permission.permission_key }}</code>
                  </td>
                  <td class="px-4 py-3">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="{
                            'bg-blue-100 text-blue-800': permission.category === 'users',
                            'bg-green-100 text-green-800': permission.category === 'trading',
                            'bg-purple-100 text-purple-800': permission.category === 'markets',
                            'bg-orange-100 text-orange-800': permission.category === 'fixtures',
                            'bg-teal-100 text-teal-800': permission.category === 'teams',
                            'bg-indigo-100 text-indigo-800': permission.category === 'players',
                            'bg-cyan-100 text-cyan-800': permission.category === 'rosters',
                            'bg-red-100 text-red-800': permission.category === 'reports',
                            'bg-pink-100 text-pink-800': permission.category === 'analytics',
                            'bg-gray-100 text-gray-800': permission.category === 'exports',
                            'bg-yellow-100 text-yellow-800': permission.category === 'admin',
                            'bg-emerald-100 text-emerald-800': permission.category === 'navigation',
                            'bg-gray-100 text-gray-800': !['users', 'trading', 'markets', 'fixtures', 'teams', 'players', 'rosters', 'reports', 'analytics', 'exports', 'admin', 'navigation'].includes(permission.category)
                          }">
                      {{ permission.category }}
                    </span>
                  </td>
                  <td class="px-4 py-3">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="{
                            'bg-green-100 text-green-800': permission.level === 1,
                            'bg-yellow-100 text-yellow-800': permission.level === 2,
                            'bg-red-100 text-red-800': permission.level === 3,
                            'bg-purple-100 text-purple-800': permission.level === 4
                          }">
                      {{ permission.level === 1 ? 'Basic' : permission.level === 2 ? 'Advanced' : permission.level === 3 ? 'Admin' : 'Super Admin' }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600">
                    {{ permission.description || 'No description available' }}
                  </td>
                </tr>

                <!-- No results message -->
                <tr v-if="filteredUserPermissions.length === 0 && userPermissionsSearchQuery && 
                         !(userPermissionsSearchQuery.toLowerCase() && (
                           'all permissions super admin'.includes(userPermissionsSearchQuery.toLowerCase()) ||
                           userPermissionsSearchQuery.toLowerCase().includes('*') ||
                           userPermissionsSearchQuery.toLowerCase().includes('super') ||
                           userPermissionsSearchQuery.toLowerCase().includes('admin') ||
                           userPermissionsSearchQuery.toLowerCase().includes('special') ||
                           userPermissionsSearchQuery.toLowerCase().includes('all') ||
                           'grants unrestricted access to all system features and functionality'.includes(userPermissionsSearchQuery.toLowerCase())
                         ))">
                  <td colspan="6" class="px-4 py-8 text-center text-gray-500">
                    <div class="flex flex-col items-center gap-2">
                      <i class="fas fa-search text-gray-400 text-2xl"></i>
                      <div>No permissions found matching "{{ userPermissionsSearchQuery }}"</div>
                      <button @click="userPermissionsSearchQuery = ''" class="text-blue-600 hover:text-blue-700 text-sm">
                        Clear search
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Loading state for user permissions -->
          <div v-else class="flex justify-center items-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>

          <!-- Permission Summary -->
          <div class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-medium text-blue-900">Permission Summary for {{ selectedUser?.name }}</h4>
              <div v-if="updatingPermissions" class="flex items-center gap-2 text-blue-600">
                <div class="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                <span class="text-sm">Updating...</span>
            </div>
              </div>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div class="text-2xl font-bold text-blue-600">{{ getTotalUserPermissions() }}</div>
                <div class="text-sm text-blue-700">Total Granted</div>
            </div>
              <div>
                <div class="text-2xl font-bold text-gray-600">{{ getTotalAvailablePermissions() }}</div>
                <div class="text-sm text-gray-700">Total Available</div>
          </div>
              <div>
                <div class="text-2xl font-bold text-green-600">
                  {{ userHasPermission('*') ? '100' : Math.round((getTotalUserPermissions() / getTotalAvailablePermissions()) * 100) }}%
                </div>
                <div class="text-sm text-green-700">Coverage</div>
            </div>
              <div>
                <div class="text-2xl font-bold" :class="userHasPermission('*') ? 'text-yellow-600' : 'text-gray-600'">
                  {{ userHasPermission('*') ? 'ADMIN' : selectedUser?.role?.toUpperCase() }}
          </div>
                <div class="text-sm" :class="userHasPermission('*') ? 'text-yellow-700' : 'text-gray-700'">Access Level</div>
            </div>
          </div>
        </div>

        <!-- League Access Management -->
        <div class="border-t pt-8">
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <i class="fas fa-trophy text-purple-600"></i>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-gray-900">League Access</h3>
                <p class="text-sm text-gray-600">Manage which leagues this user can access</p>
              </div>
            </div>
            
            <!-- Quick Actions -->
            <div class="flex items-center gap-2">
              <button 
                @click="grantAllLeagues"
                class="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
              >
                <i class="fas fa-check-double"></i>
                Grant All
              </button>
              <button 
                @click="removeAllLeagues"
                class="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center gap-2"
              >
                <i class="fas fa-times-circle"></i>
                Remove All
              </button>
            </div>
          </div>

          <!-- Search Filter for Leagues -->
          <div class="flex items-center gap-4 mb-4">
            <div class="relative flex-1 max-w-md">
              <input 
                v-model="leaguesSearchQuery"
                type="text" 
                placeholder="Search leagues by name, sport, or country..."
                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
              <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
            </div>
            <div v-if="leaguesSearchQuery" class="text-sm text-gray-600">
              {{ filteredLeagues.length }} of {{ availableLeagues.length }} leagues
            </div>
          </div>

          <!-- Loading state -->
          <div v-if="loadingLeagues" class="flex justify-center items-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>

          <!-- Leagues Grid -->
          <div v-else-if="availableLeagues.length > 0" class="bg-white border rounded-lg overflow-hidden">
            <div class="max-h-96 overflow-y-auto">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
                <div 
                  v-for="league in filteredLeagues" 
                  :key="league.id"
                  class="border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md"
                  :class="userHasLeagueAccess(league.id) ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'"
                  @click="toggleLeagueAccess(league.id)"
                >
                  <div class="flex items-center gap-3">
                    <!-- Checkbox indicator -->
                    <div 
                      class="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 transition-all duration-200"
                      :class="userHasLeagueAccess(league.id) ? 'bg-green-500' : 'bg-gray-300'"
                    >
                      <i v-if="userHasLeagueAccess(league.id)" class="fas fa-check text-white text-sm"></i>
                      <i v-else class="fas fa-times text-white text-sm"></i>
                    </div>
                    
                    <!-- League Info -->
                    <div class="flex-1 min-w-0">
                      <div class="font-medium text-sm text-gray-900 truncate">
                        {{ league.name || 'Unknown League' }}
                      </div>
                      <div class="flex items-center gap-2 mt-1">
                        <span v-if="league.sport" class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {{ league.sport }}
                        </span>
                        <span v-if="league.country" class="text-xs text-gray-500">
                          {{ league.country }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No results message -->
              <div v-if="filteredLeagues.length === 0 && leaguesSearchQuery" 
                   class="px-4 py-8 text-center text-gray-500">
                <div class="flex flex-col items-center gap-2">
                  <i class="fas fa-search text-gray-400 text-2xl"></i>
                  <div>No leagues found matching "{{ leaguesSearchQuery }}"</div>
                  <button @click="leaguesSearchQuery = ''" class="text-blue-600 hover:text-blue-700 text-sm">
                    Clear search
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- No leagues available -->
          <div v-else class="text-center py-8 bg-gray-50 rounded-lg">
            <div class="text-gray-400 mb-4">
              <i class="fas fa-trophy text-4xl"></i>
            </div>
            <div class="text-gray-600">No leagues available</div>
          </div>

          <!-- League Access Summary -->
          <div class="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-medium text-purple-900">League Access Summary</h4>
              <div v-if="updatingLeagueAccess" class="flex items-center gap-2 text-purple-600">
                <div class="animate-spin w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                <span class="text-sm">Updating...</span>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-4 text-center">
              <div>
                <div class="text-2xl font-bold text-purple-600">
                  {{ selectedUser?.league_access?.length || 0 }}
                </div>
                <div class="text-sm text-purple-700">Leagues Granted</div>
              </div>
              <div>
                <div class="text-2xl font-bold text-gray-600">
                  {{ availableLeagues.length }}
                </div>
                <div class="text-sm text-gray-700">Total Available</div>
              </div>
              <div>
                <div class="text-2xl font-bold text-green-600">
                  {{ availableLeagues.length > 0 ? Math.round(((selectedUser?.league_access?.length || 0) / availableLeagues.length) * 100) : 0 }}%
                </div>
                <div class="text-sm text-green-700">Coverage</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <AddUserForm v-if="showAddUserForm" @close="showAddUserForm = false" @success="showAddUserForm = false; userStore.fetchUsers()" />

  <!-- Permission Users Modal -->
  <div v-if="showPermissionUsersModal" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center overflow-y-auto"
       @click.self="closePermissionUsersModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl m-8">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Users with Permission</h3>
          <p class="text-sm text-gray-600 mt-1">
            Users who have the permission: 
            <code class="bg-gray-100 px-2 py-1 rounded text-sm">{{ selectedPermissionKey }}</code>
          </p>
        </div>
        <button @click="closePermissionUsersModal" 
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-6">
        <!-- Summary -->
        <div class="flex items-center gap-4 mb-6 p-4 bg-blue-50 rounded-lg">
          <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <i class="fas fa-users text-blue-600"></i>
          </div>
          <div>
            <div class="text-lg font-semibold text-blue-900">
              {{ usersWithSelectedPermission.length }} 
              {{ usersWithSelectedPermission.length === 1 ? 'user has' : 'users have' }} this permission
            </div>
            <div class="text-sm text-blue-700">
              {{ selectedPermissionKey === '*' ? 'Super admin access' : 'Specific permission grant' }}
            </div>
          </div>
        </div>

        <!-- Users List -->
        <div v-if="usersWithSelectedPermission.length > 0" class="space-y-3">
          <h4 class="font-medium text-gray-900 mb-3">Users:</h4>
          <div 
            v-for="user in usersWithSelectedPermission" 
            :key="user.id"
            class="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            <!-- User Avatar -->
            <div class="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
              <span class="text-gray-600 font-medium">
                {{ user.name ? user.name.charAt(0) : (user.email ? user.email.charAt(0) : '?') }}
              </span>
            </div>
            
            <!-- User Info -->
            <div class="flex-1">
              <div class="font-medium text-gray-900">{{ user.name || 'Unknown User' }}</div>
              <div class="text-sm text-gray-500">{{ user.email || 'No email' }}</div>
            </div>
            
            <!-- User Role -->
            <div class="flex items-center gap-2">
              <span class="px-2 py-1 text-xs rounded-full"
                    :class="{
                      'bg-yellow-100 text-yellow-800': user.role === 'admin',
                      'bg-green-100 text-green-800': user.role === 'trader',
                      'bg-blue-100 text-blue-800': user.role === 'client',
                      'bg-gray-100 text-gray-800': !user.role
                    }">
                {{ user.role || 'No Role' }}
              </span>
              
              <!-- Permission Source Indicator -->
              <span v-if="user.permissions?.includes('*')" 
                    class="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full"
                    title="Has permission via super admin access">
                <i class="fas fa-crown mr-1"></i>
                Super Admin
              </span>
              <span v-else-if="user.permissions?.includes(selectedPermissionKey)" 
                    class="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full"
                    title="Has permission directly assigned">
                <i class="fas fa-check mr-1"></i>
                Direct
              </span>
            </div>
            
            <!-- Actions -->
            <button 
              @click="viewUserDetails(user); closePermissionUsersModal()"
              class="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              title="View user details"
            >
              <i class="fas fa-eye mr-1"></i>
              View
            </button>
          </div>
        </div>
        
        <!-- No Users State -->
        <div v-else class="text-center py-8">
          <div class="text-gray-400 mb-4">
            <i class="fas fa-users-slash text-4xl"></i>
          </div>
          <div class="text-gray-600">
            No users currently have this permission
          </div>
          <div class="text-sm text-gray-500 mt-2">
            This permission is available but not assigned to any users
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="closePermissionUsersModal"
          class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>

  <!-- Add Permission Modal -->
  <div v-if="showAddPermissionModal" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-center justify-center overflow-y-auto"
       @click.self="closeAddPermissionModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg m-8">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Add New Permission</h3>
          <p class="text-sm text-gray-600 mt-1">Create a new permission for the system</p>
        </div>
        <button @click="closeAddPermissionModal" 
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Form -->
      <div class="p-6 space-y-4">
        <!-- Permission Key -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Permission Key <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="newPermission.permission_key"
            type="text" 
            placeholder="e.g., fixtures.view or users.create"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :class="{ 'border-red-300': !newPermission.permission_key }"
          />
          <p class="text-xs text-gray-500 mt-1">
            Use dot notation (e.g., category.action) or snake_case
          </p>
        </div>

        <!-- Permission Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Permission Name <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="newPermission.name"
            type="text" 
            placeholder="e.g., View Fixtures or Create Users"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :class="{ 'border-red-300': !newPermission.name }"
          />
        </div>

        <!-- Description -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea 
            v-model="newPermission.description"
            rows="3"
            placeholder="Describe what this permission allows users to do..."
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>

        <!-- Category -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select 
            v-model="newPermission.category"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option v-for="category in getAvailableCategories()" :key="category" :value="category">
              {{ category }}
            </option>
          </select>
        </div>

        <!-- Level -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Permission Level
          </label>
          <select 
            v-model="newPermission.level"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option :value="1">Basic (1) - Standard user access</option>
            <option :value="2">Advanced (2) - Enhanced features</option>
            <option :value="3">Admin (3) - Administrative access</option>
            <option :value="4">Super Admin (4) - Full system access</option>
          </select>
        </div>

        <!-- Sort Order -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Sort Order
          </label>
          <input 
            v-model="newPermission.sort_order"
            type="number" 
            min="0"
            max="999"
            placeholder="0"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p class="text-xs text-gray-500 mt-1">
            Lower numbers appear first in lists
          </p>
        </div>

        <!-- Checkboxes -->
        <div class="space-y-2">
          <label class="flex items-center">
            <input 
              v-model="newPermission.is_active"
              type="checkbox" 
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">Active (permission can be assigned)</span>
          </label>
          
          <label class="flex items-center">
            <input 
              v-model="newPermission.is_system"
              type="checkbox" 
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">System permission (core functionality)</span>
          </label>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="closeAddPermissionModal"
          class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          :disabled="addingPermission"
        >
          Cancel
        </button>
        <button 
          @click="createPermission"
          :disabled="addingPermission || !newPermission.permission_key || !newPermission.name"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <div v-if="addingPermission" class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
          <i v-else class="fas fa-plus"></i>
          {{ addingPermission ? 'Creating...' : 'Create Permission' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Generate Token Modal -->
  <div v-if="showGenerateTokenModal" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-center justify-center overflow-y-auto"
       @click.self="showGenerateTokenModal = false"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg m-8">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Generate API Token</h3>
          <p class="text-sm text-gray-600 mt-1">Create a new API access token for a user</p>
        </div>
        <button @click="showGenerateTokenModal = false" 
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Form -->
      <div class="p-6 space-y-4">
        <!-- User Selection -->
        <div v-if="!selectedUserForTokens">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Select User <span class="text-red-500">*</span>
          </label>
          <select 
            v-model="selectedUserForTokens"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Choose a user...</option>
            <option v-for="user in userStore.users" :key="user.id" :value="user">
              {{ user.name }} ({{ user.email }})
            </option>
          </select>
        </div>

        <!-- Token Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Token Name <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="newTokenName"
            type="text" 
            placeholder="e.g., API Integration, Mobile App, Webhook"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :class="{ 'border-red-300': !newTokenName }"
          />
          <p class="text-xs text-gray-500 mt-1">
            Give your token a descriptive name to identify its purpose
          </p>
        </div>

        <!-- Token Abilities -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Token Abilities
          </label>
          <div class="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
            <label v-for="ability in availableTokenAbilities" :key="ability.key" class="flex items-start gap-3">
              <input 
                v-model="newTokenAbilities"
                type="checkbox" 
                :value="ability.key"
                class="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div class="flex-1">
                <div class="text-sm font-medium text-gray-900">{{ ability.name }}</div>
                <div class="text-xs text-gray-500">{{ ability.description }}</div>
              </div>
            </label>
          </div>
          <p class="text-xs text-gray-500 mt-1">
            Select the permissions this token should have
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="showGenerateTokenModal = false"
          class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          :disabled="generatingToken"
        >
          Cancel
        </button>
        <button 
          @click="generateNewToken"
          :disabled="generatingToken || !newTokenName || !selectedUserForTokens"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <div v-if="generatingToken" class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
          <i v-else class="fas fa-key"></i>
          {{ generatingToken ? 'Generating...' : 'Generate Token' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Token Details Modal -->
  <div v-if="showTokenDetailsModal" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto"
       @click.self="closeTokenDetailsModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg m-8">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Token Details</h3>
          <p class="text-sm text-gray-600 mt-1">View full token information</p>
        </div>
        <button @click="closeTokenDetailsModal" 
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-6">
        <div v-if="selectedToken" class="space-y-4">
          <!-- Security Notice -->
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div class="flex items-center gap-2 text-yellow-800">
              <i class="fas fa-shield-alt"></i>
              <span class="font-medium">Security Notice</span>
            </div>
            <p class="text-sm text-yellow-700 mt-1">
              To view the full token, please enter your admin password for verification.
            </p>
          </div>

          <!-- Password Input -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Admin Password
            </label>
            <input 
              v-model="adminPasswordForToken"
              type="password" 
              placeholder="Enter your admin password"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              @keyup.enter="verifyTokenAccess"
            />
            <p v-if="tokenAccessError" class="mt-1 text-sm text-red-600">
              {{ tokenAccessError }}
            </p>
          </div>

          <!-- Token Details (shown after verification) -->
          <div v-if="!tokenAccessError && adminPasswordForToken" class="space-y-4">
            <!-- Token Display -->
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-700">Full Token:</span>
                <button 
                  @click="copyTokenToClipboard(selectedToken.token)"
                  class="text-blue-600 hover:text-blue-700 text-sm"
                  title="Copy token"
                >
                  <i class="fas fa-copy"></i>
                </button>
              </div>
              <div class="font-mono text-sm break-all bg-white p-2 rounded border">
                {{ selectedToken.token }}
              </div>
            </div>

            <!-- Token Information -->
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span class="font-medium text-gray-700">Name:</span>
                <div class="text-gray-900">{{ selectedToken.name }}</div>
              </div>
              <div>
                <span class="font-medium text-gray-700">ID:</span>
                <div class="text-gray-900">{{ selectedToken.id }}</div>
              </div>
              <div>
                <span class="font-medium text-gray-700">Created:</span>
                <div class="text-gray-900">{{ new Date(selectedToken.created_at).toLocaleString() }}</div>
              </div>
              <div>
                <span class="font-medium text-gray-700">Last Used:</span>
                <div class="text-gray-900">{{ formatLastUsed(selectedToken.last_used_at) }}</div>
              </div>
              <div>
                <span class="font-medium text-gray-700">Expires:</span>
                <div class="text-gray-900">
                  {{ selectedToken.expires_at ? new Date(selectedToken.expires_at).toLocaleString() : 'Never' }}
                </div>
              </div>
              <div>
                <span class="font-medium text-gray-700">Status:</span>
                <div>
                  <span :class="[
                    'px-2 py-1 text-xs rounded-full font-medium',
                    getTokenStatus(selectedToken).class
                  ]">
                    {{ getTokenStatus(selectedToken).status }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Abilities -->
            <div>
              <span class="font-medium text-gray-700 text-sm">Abilities:</span>
              <div class="flex flex-wrap gap-2 mt-2">
                <span v-for="ability in (selectedToken.abilities || [])" :key="ability"
                      class="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {{ ability === '*' ? 'All Permissions' : ability }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="verifyTokenAccess"
          :disabled="verifyingTokenAccess || !adminPasswordForToken"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <div v-if="verifyingTokenAccess" class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
          <i v-else class="fas fa-eye"></i>
          {{ verifyingTokenAccess ? 'Verifying...' : 'View Token' }}
        </button>
        <button 
          @click="closeTokenDetailsModal"
          class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>

  <!-- Password Change Modal -->
  <div v-if="showPasswordChangeModal" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[110] flex items-center justify-center overflow-y-auto"
       @click.self="closePasswordChangeModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md m-8">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Change User Password</h3>
          <p class="text-sm text-gray-600 mt-1">
            Update password for {{ selectedUser?.name || selectedUser?.email }}
          </p>
        </div>
        <button @click="closePasswordChangeModal" 
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Form -->
      <div class="p-6 space-y-4">
        <!-- Success Message -->
        <div v-if="passwordChangeSuccess" 
             class="bg-green-50 border border-green-200 rounded-lg p-4">
          <div class="flex items-center gap-2 text-green-800">
            <i class="fas fa-check-circle"></i>
            <span class="font-medium">Password updated successfully!</span>
          </div>
          <p class="text-sm text-green-700 mt-1">
            The user will need to use the new password on their next login.
          </p>
        </div>

        <!-- Error Message -->
        <div v-if="passwordChangeError" 
             class="bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-center gap-2 text-red-800">
            <i class="fas fa-exclamation-circle"></i>
            <span class="font-medium">Error</span>
          </div>
          <p class="text-sm text-red-700 mt-1">{{ passwordChangeError }}</p>
        </div>

        <!-- Current Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Current Password <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="passwordChangeForm.currentPassword"
            type="password" 
            placeholder="Enter current password"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :disabled="changingPassword"
          />
          <p class="text-xs text-gray-500 mt-1">
            Enter the user's current password to verify identity
          </p>
        </div>

        <!-- New Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            New Password <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="passwordChangeForm.newPassword"
            type="password" 
            placeholder="Enter new password (min 8 characters)"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :disabled="changingPassword"
          />
          <p class="text-xs text-gray-500 mt-1">
            Must be at least 8 characters long
          </p>
        </div>

        <!-- Confirm New Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Confirm New Password <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="passwordChangeForm.confirmPassword"
            type="password" 
            placeholder="Confirm new password"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :disabled="changingPassword"
            @keyup.enter="updateUserPassword"
          />
          <p class="text-xs text-gray-500 mt-1">
            Re-enter the new password to confirm
          </p>
        </div>

        <!-- Password Requirements -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-start gap-2 text-blue-800">
            <i class="fas fa-info-circle mt-1"></i>
            <div>
              <div class="font-medium text-sm">Password Requirements:</div>
              <ul class="text-xs text-blue-700 mt-1 space-y-1">
                <li>• Minimum 8 characters long</li>
                <li>• Should be different from current password</li>
                <li>• Consider using a mix of letters, numbers, and symbols</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="closePasswordChangeModal"
          class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          :disabled="changingPassword"
        >
          Cancel
        </button>
        <button 
          @click="updateUserPassword"
          :disabled="changingPassword || !passwordChangeForm.currentPassword || !passwordChangeForm.newPassword || !passwordChangeForm.confirmPassword"
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <div v-if="changingPassword" class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
          <i v-else class="fas fa-key"></i>
          {{ changingPassword ? 'Updating...' : 'Update Password' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Organization Team Modal -->
  <div v-if="showOrganizationTeamModal" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-8"
       @click.self="closeOrganizationTeamModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b">
        <div class="flex items-center gap-4">
          <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <i class="fas fa-building text-blue-600"></i>
          </div>
          <div>
            <h3 class="text-xl font-semibold text-gray-900">{{ selectedOrganizationTeam }}</h3>
            <p class="text-sm text-gray-600">{{ organizationTeamUsers.length }} {{ organizationTeamUsers.length === 1 ? 'team member' : 'team members' }}</p>
          </div>
        </div>
        <button @click="closeOrganizationTeamModal" 
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-6 flex-1 overflow-y-auto">
        <!-- Team Stats -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div class="text-2xl font-bold text-blue-600">{{ organizationTeamUsers.length }}</div>
            <div class="text-sm text-blue-700">Total Members</div>
          </div>
          <div class="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div class="text-2xl font-bold text-green-600">
              {{ organizationTeamUsers.filter(u => u.status === 'active').length }}
            </div>
            <div class="text-sm text-green-700">Active</div>
          </div>
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <div class="text-2xl font-bold text-yellow-600">
              {{ organizationTeamUsers.filter(u => u.role === 'admin').length }}
            </div>
            <div class="text-sm text-yellow-700">Admins</div>
          </div>
          <div class="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
            <div class="text-2xl font-bold text-purple-600">
              {{ organizationTeamUsers.filter(u => u.role === 'trader').length }}
            </div>
            <div class="text-sm text-purple-700">Traders</div>
          </div>
        </div>

        <!-- Team Members List -->
        <div class="space-y-3">
          <h4 class="font-medium text-gray-900 mb-4">Team Members:</h4>
          <div 
            v-for="user in organizationTeamUsers" 
            :key="user.id"
            class="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            @click="viewUserDetails(user); closeOrganizationTeamModal()"
          >
            <!-- User Avatar -->
            <div class="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
              <span class="text-gray-600 font-medium text-lg">
                {{ user.name ? user.name.charAt(0) : (user.email ? user.email.charAt(0) : '?') }}
              </span>
            </div>
            
            <!-- User Info -->
            <div class="flex-1">
              <div class="font-medium text-gray-900">{{ user.name || 'Unknown User' }}</div>
              <div class="text-sm text-gray-500">{{ user.email || 'No email' }}</div>
            </div>
            
            <!-- User Status & Role -->
            <div class="flex items-center gap-2">
              <span class="px-2 py-1 text-xs rounded-full"
                    :class="{
                      'bg-yellow-100 text-yellow-800': user.role === 'admin',
                      'bg-green-100 text-green-800': user.role === 'trader',
                      'bg-blue-100 text-blue-800': user.role === 'client',
                      'bg-gray-100 text-gray-800': !user.role
                    }">
                {{ user.role || 'No Role' }}
              </span>
              
              <span class="px-2 py-1 text-xs rounded-full"
                    :class="{
                      'bg-green-100 text-green-800': user.status === 'active',
                      'bg-red-100 text-red-800': user.status === 'inactive',
                      'bg-yellow-100 text-yellow-800': user.status === 'pending'
                    }">
                {{ user.status || 'Unknown' }}
              </span>
            </div>
            
            <!-- Last Active -->
            <div class="text-sm text-gray-500">
              {{ user.lastActive ? new Date(user.lastActive).toLocaleString() : 'Never' }}
            </div>
            
            <!-- View Details Button -->
            <button 
              class="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              title="View user details"
            >
              <i class="fas fa-eye"></i>
            </button>
          </div>
        </div>

        <!-- No team members -->
        <div v-if="organizationTeamUsers.length === 0" class="text-center py-8">
          <div class="text-gray-400 mb-4">
            <i class="fas fa-users-slash text-4xl"></i>
          </div>
          <div class="text-gray-600">No team members found</div>
          <div class="text-sm text-gray-500 mt-2">
            This organization doesn't have any users assigned
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="closeOrganizationTeamModal"
          class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template> 